import i18next from "i18next";
import { Box, Typography } from "@mui/material";

const defaultOutcomes = ["1", "X", "2"];
export const getPerfectTipsOutcomeOrder = (outcomes) => {
  const keys = Object.keys(outcomes || {});
  return keys.length === defaultOutcomes.length &&
    defaultOutcomes.every((outcome) => keys.includes(outcome))
    ? defaultOutcomes
    : keys;
};
const text = (key, values) =>
  i18next.t(`help.leagueAnalysis.perfectTips.${key}`, values);
const signedUnits = (value) => {
  if (!Number.isFinite(value)) return "–";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(2)}`;
};
const probability = (value) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "–";

function OutcomeGrid({ children, emphasized = false, columns = 3 }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 0.25, px: 0.45, py: 0.22, bgcolor: emphasized ? "rgba(126, 53, 88, 0.055)" : "rgba(0, 0, 0, 0.025)" }}>
      {children}
    </Box>
  );
}

export function PerfectTipsHeader({ display, outcomes = defaultOutcomes, market = "1X2" }) {
  const probabilityView = display === "probabilities";
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 72px", gap: 0.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: probabilityView ? "1fr 1fr" : "1fr", gap: 0.5 }}>
        {(probabilityView ? ["betty", "market100"] : [display]).map((key) => (
          <Box key={key} sx={{ textAlign: "center", py: 0.2 }}>
            <Typography variant="caption" display="block" sx={{ fontWeight: 900 }}>
              {text(key, { market })}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${outcomes.length}, 1fr)` }}>
              {outcomes.map((outcome) => <Typography key={outcome} variant="caption" sx={{ fontWeight: 900 }}>{outcome}</Typography>)}
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" align="center" sx={{ fontWeight: 900 }}>{text("total")}</Typography>
    </Box>
  );
}

export function PerfectTipsFixtureValues({ result, display }) {
  const probabilityView = display === "probabilities";
  const realizedValue = result?.realizedValue;
  const orderedOutcomes = getPerfectTipsOutcomeOrder(result?.outcomes);
  const outcomes = orderedOutcomes.length ? orderedOutcomes : defaultOutcomes;
  const renderValues = (source) => (
    <OutcomeGrid emphasized={source === "probabilityA"} columns={outcomes.length}>
      {outcomes.map((outcome) => {
        const entry = result?.outcomes?.[outcome];
        const realized = display === "payout" && result?.realizedOutcome === outcome;
        const value = source === "stake"
          ? Number.isFinite(entry?.stake) ? entry.stake.toFixed(2) : "–"
          : source === "value"
            ? signedUnits(entry?.value)
            : probability(entry?.[source]);
        return (
          <Typography
            key={outcome}
            variant="body2"
            noWrap
            align="center"
            color={realized ? (entry.value >= 0 ? "success.main" : "error.main") : "text.primary"}
            sx={{ py: 0.35, fontSize: "0.7rem", fontWeight: realized ? 900 : 800, fontVariantNumeric: "tabular-nums", border: "1px solid", borderColor: realized ? (entry.value >= 0 ? "success.main" : "error.main") : "transparent", borderRadius: 0.5 }}
          >
            {value}
          </Typography>
        );
      })}
    </OutcomeGrid>
  );

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 72px", gap: 0.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: probabilityView ? "1fr 1fr" : "1fr", gap: 0.5 }}>
        {probabilityView ? <>{renderValues("probabilityA")}{renderValues("probabilityB")}</> : renderValues(display === "stake" ? "stake" : "value")}
      </Box>
      <Typography
        variant="caption"
        noWrap
        align="center"
        color={display === "payout" && Number.isFinite(realizedValue) ? (realizedValue >= 0 ? "success.main" : "error.main") : "text.primary"}
        sx={{ py: 0.57, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}
      >
        {display === "probabilities" ? (result ? "100.0%" : "–") : display === "stake" ? (result ? result.totalStake.toFixed(2) : "–") : signedUnits(realizedValue)}
      </Typography>
    </Box>
  );
}

export function PerfectTipsSummary({ summary }) {
  return (
    <Box sx={{ mx: 1, my: 0.75, px: 1, py: 0.65, display: "flex", justifyContent: "flex-end", bgcolor: "action.hover", borderRadius: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 900 }}>
        {text("total")}: {signedUnits(summary.fixtures ? summary.totalValue : null)}
      </Typography>
    </Box>
  );
}
